import { usePlayer, useStage } from "@empirica/core/player/classic/react";
import React, { useState, useEffect } from "react";

export function WaitingForOthers({ stageName }) {
  const player = usePlayer();
  const stage = useStage();
  const [waitTime, setWaitTime] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setWaitTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStageDescription = (stageName) => {
    switch (stageName) {
      case "choice":
        return "making their initial predictions";
      case "discussion":
        return "sharing their thoughts in the discussion";
      case "aiDiscuss":
        return "reviewing the AI-generated discussion";
      case "assistant":
        return "interacting with the AI assistant";
      case "finalVote":
        return "casting their final votes";
      default:
        return "completing their tasks";
    }
  };

  const getEncouragingMessage = () => {
    const messages = [
      "Thanks for your patience!",
      "Your input is valuable to our research.",
      "This helps us understand group decision-making.",
      "Great job staying engaged!",
      "Your participation makes a difference."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  return (
    <div className="text-center space-y-6 max-w-2xl mx-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Waiting for Other Participants
          </h2>

          <div className="text-sm text-gray-600 mb-4">
            <span className="font-medium">Wait time: </span>
            <span className="font-mono text-lg text-blue-600">{formatTime(waitTime)}</span>
          </div>
        </div>

        <div className="space-y-4 text-left">
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h3 className="font-semibold text-gray-800 mb-2">What's happening?</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Other participants are currently <strong>{getStageDescription(stageName)}</strong>.
              Once everyone has completed this stage, the game will continue automatically.
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h3 className="font-semibold text-gray-800 mb-2">Current Stage</h3>
            <p className="text-gray-700 text-sm">
              You're waiting at the <strong className="text-blue-600 capitalize">{stageName}</strong> stage.
              This is an important part of the collaborative decision-making process.
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-green-800 text-sm font-medium">
              {getEncouragingMessage()}
            </p>
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          <p>Please keep this window open. The game will resume automatically.</p>
        </div>
      </div>
    </div>
  );
}
